/** @format */

import React, { useState, useMemo, useCallback } from 'react';
import { Table, Button, Input, Space, InputNumber, DatePicker, Select, Popconfirm } from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { Policy } from '../services/getPolicies';
import CampaignDropdown from './CampaignDropdown';
import UsersDropdown from './UsersDropdown';
import OfficeDropdown from './OfficeDropdown';
import dayjs from 'dayjs';

interface PoliciesTableProps {
  data: Partial<Policy>[];
  loading: boolean;
  error?: any;
  onUpdate?: (policyId: string, nestedRecord: any, updateData: Record<string, any>) => Promise<void>;
  onDelete?: (policyId: string) => Promise<void>;
  onFilteredDataChange?: (filteredData: Partial<Policy>[]) => void;
}

const PoliciesTable: React.FC<PoliciesTableProps> = ({ data, loading, error, onUpdate, onDelete, onFilteredDataChange }) => {
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [tableFilters, setTableFilters] = useState<Record<string, any>>({});

  // Get user role from Redux
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const userRole = userData?.role;
  const isRootUser = userRole && ['root_user', 'root_custo'].includes(userRole);

  const capitalizeFullName = (name?: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const dateFormat = (date?: string) => {
    if (!date) return '';
    // Parse as YYYY-MM-DD string to avoid timezone offset issues
    const parts = date.split('T')[0].split('-');
    if (parts.length !== 3) return '';
    const month = parts[1];
    const day = parts[2];
    const year = parts[0];
    return `${month}/${day}/${year}`;
  };

  const getStatusClass = (status?: string) => {
    switch (status) {
      case 'Cancel':
        return 'status-cancel';
      case 'Declined':
        return 'status-declined';
      case 'Paid':
        return 'status-paid';
      default:
        return '';
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value: string, record: any) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInlineUpdate = async (parentId: string, nestedRecord?: any) => {
    // For main table edits, editKey is 'campaign-{id}', for nested it's just the _id
    const editKey = nestedRecord?.nestedType ? nestedRecord._id : `campaign-${parentId}`;
    
    if (onUpdate && editValues[editKey]) {
      try {
        if (nestedRecord?.nestedType) {
          // Nested record update
          await onUpdate(parentId, nestedRecord, editValues[editKey]);
        } else {
          // Main table record update - nestedRecord is actually the full record
          await onUpdate(parentId, nestedRecord || {}, editValues[editKey]);
        }
        setEditingId(null);
        setEditValues({});
      } catch (error) {
        console.error('Failed to update policy:', error);
      }
    }
  };

  const handleEditFieldChange = useCallback((recordId: string, field: string, value: any) => {
    setEditValues((prev) => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        [field]: value,
      },
    }));
  }, []);

  const getCampaignChangeHandler = useCallback((recordId: string) => {
    return (vendorName: string) => handleEditFieldChange(recordId, 'vendorName', vendorName);
  }, [handleEditFieldChange]);

  const getOfficeChangeHandler = useCallback((recordId: string) => {
    return (officeId: string) => handleEditFieldChange(recordId, 'officeId', officeId);
  }, [handleEditFieldChange]);

  const getUserChangeHandler = useCallback((recordId: string) => {
    return (userId: string) => handleEditFieldChange(recordId, 'userId', userId);
  }, [handleEditFieldChange]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValues({});
  }, []);

  const columns: any[] = useMemo(() => [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      ...getColumnSearchProps('firstName'),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      ...getColumnSearchProps('lastName'),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      ...getColumnSearchProps('phoneNumber'),
      render: (text: string, record: any) => {
        const isEditing = editingId === `campaign-${record._id}`;
        if (!isEditing) {
          return <span>{text}</span>;
        }
        
        return (
          <Input
            value={editValues[`campaign-${record._id}`]?.phoneNumber ?? text}
            onChange={(e) => handleEditFieldChange(`campaign-${record._id}`, 'phoneNumber', e.target.value)}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: 'memberId',
      dataIndex: 'memberId',
      key: 'memberId',
      ...getColumnSearchProps('memberId'),
    },
    {
     title:"Campaign",
     dataIndex:'vendorName',
     key:'vendorName',
     ...getColumnSearchProps('vendorName'),
     render: (text: string, record: any) => {
       const isEditing = editingId === `campaign-${record._id}`;
       if (!isEditing) {
         return <span>{text}</span>;
       }
       
       return (
         <CampaignDropdown
           officeId={record.officeId}
           value={editValues[`campaign-${record._id}`]?.vendorName || text}
           onChange={getCampaignChangeHandler(`campaign-${record._id}`)}
         />
       );
     },
    },
    {
      title: 'Office',
      dataIndex: ['office', 'officeName'],
      key: 'officeName',
      ...getColumnSearchProps('officeName'),
      render: (text: string, record: any) => {
        const isEditing = editingId === `campaign-${record._id}`;
        
        if (!isEditing || !isRootUser) {
          return <span>{text}</span>;
        }
        
        return (
          <OfficeDropdown
            value={editValues[`campaign-${record._id}`]?.officeId || record.officeId}
            onChange={getOfficeChangeHandler(`campaign-${record._id}`)}
          />
        );
      },
    },
    {
      title: 'Agent Name',
      dataIndex: ['user', 'firstName'],
      key: 'agentName',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Agent"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm, 'agentName')}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, 'agentName')}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value: string, record: any) => {
        const fullName = `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.toLowerCase();
        return fullName.includes(value.toLowerCase());
      },
      render: (text: string, record: any) => {
        const isEditing = editingId === `campaign-${record._id}`;
        const agentName = capitalizeFullName(`${record.user?.firstName} ${record.user?.lastName}`);
        
        if (!isEditing) {
          return <span>{agentName}</span>;
        }
        
        return (
          <UsersDropdown
            officeId={record.officeId}
            value={editValues[`campaign-${record._id}`]?.userId || record.user?._id}
            onChange={getUserChangeHandler(`campaign-${record._id}`)}
          />
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Paid', value: 'Paid' },
        { text: 'Cancel', value: 'Cancel' },
        { text: 'Declined', value: 'Declined' },
      ],
      onFilter: (value: string, record: any) => record.status === value,
      render: (text: string) => <span className={getStatusClass(text)}>{text}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      render: (_: any, record: any) => {
        const isEditing = editingId === `campaign-${record._id}`;
        return isEditing ? (
          <Space>
            <Button 
              type="primary" 
              size="small"
              onClick={() => handleInlineUpdate(record._id, { ...record, _id: record._id })}
            >
              Done
            </Button>
            <Button 
              size="small"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
          </Space>
        ) : (
          <Space>
            <Button 
              type="primary" 
              size="small"
              onClick={() => setEditingId(`campaign-${record._id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete Policy"
              description="Are you sure you want to delete this policy?"
              onConfirm={() => onDelete?.(record._id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ], [editingId, editValues, handleEditFieldChange, handleCancelEdit, getCampaignChangeHandler, getColumnSearchProps, getUserChangeHandler, handleInlineUpdate, onDelete]);

  const expandedRowRender = (record: any) => {
    const policyItems = [];

    if (record.majorMed) {
      policyItems.push({ 
        ...record.majorMed, 
        type: 'Major Med', 
        _id: record.majorMed._id || `majorMed-${record._id}`, 
        parentId: record._id, 
        nestedType: 'majorMed' 
      });
    }
    if (record.privateMed) {
      policyItems.push({ 
        ...record.privateMed, 
        type: 'Private Med', 
        _id: record.privateMed._id || `privateMed-${record._id}`, 
        parentId: record._id, 
        nestedType: 'privateMed' 
      });
    }
    if (record.ancillaries && Array.isArray(record.ancillaries)) {
      record.ancillaries.forEach((item: any, index: number) => {
        if (item && Object.values(item).some((v) => v)) {
          policyItems.push({ ...item, type: 'Ancillary', _id: item._id || `ancillary-${record._id}-${index}`, parentId: record._id, nestedType: 'ancillaries', ancillaryIndex: index });
        }
      });
    }

    const nestedColumns = [
      { title: 'Type', dataIndex: 'type', key: 'type' },
      { title: 'Carrier', dataIndex: 'carrier', key: 'carrier' },
      {
        title: 'Billing Date',
        dataIndex: 'billingDate',
        key: 'billingDate',
        render: (text: string, nestedRecord: any) => 
          editingId && editingId === nestedRecord._id ? (
            <DatePicker
              value={editValues[nestedRecord._id]?.billingDate ? dayjs(editValues[nestedRecord._id].billingDate, 'YYYY-MM-DD') : (text ? dayjs(text, 'YYYY-MM-DD') : null)}
              onChange={(date) => {
                const dateStr = date ? date.format('YYYY-MM-DD') : '';
                handleEditFieldChange(nestedRecord._id, 'billingDate', dateStr);
              }}
              style={{ width: '100%' }}
              autoFocus
            />
          ) : (
            <span>{dateFormat(text)}</span>
          ),
      },
      { 
        title: 'Premium', 
        dataIndex: 'premium', 
        key: 'premium',
        render: (text: any, nestedRecord: any) =>
          editingId && editingId === nestedRecord._id ? (
            <InputNumber
              value={editValues[nestedRecord._id]?.premium !== undefined ? editValues[nestedRecord._id].premium : text}
              onChange={(value) => handleEditFieldChange(nestedRecord._id, 'premium', value)}
              min={0}
              step={0.01}
              style={{ width: '100%' }}
              autoFocus
            />
          ) : (
            <span>{text}</span>
          ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (text: string, nestedRecord: any) =>
          editingId && editingId === nestedRecord._id ? (
            <Select
              value={editValues[nestedRecord._id]?.status || text || undefined}
              onChange={(value) => handleEditFieldChange(nestedRecord._id, 'status', value)}
              style={{ width: '100%' }}
              options={[
                { label: 'Paid', value: 'Paid' },
                { label: 'Cancel', value: 'Cancel' },
                { label: 'Declined', value: 'Declined' },
                { label: 'Pending', value: 'Pending' },
              ]}
              autoFocus
            />
          ) : (
            <span className={getStatusClass(text)}>{text}</span>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        width: 150,
        render: (_: any, nestedRecord: any) =>
          editingId && editingId === nestedRecord._id ? (
            <>
              <Button 
                type="primary" 
                size="small"
                onClick={() => handleInlineUpdate(nestedRecord.parentId, nestedRecord)}
                style={{ marginRight: '8px' }}
              >
                Done
              </Button>
              <Button 
                size="small"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              type="primary" 
              size="small"
              onClick={() => setEditingId(nestedRecord._id)}
            >
              Edit
            </Button>
          ),
      },
    ];

    return (
      <Table
        columns={nestedColumns}
        dataSource={policyItems}
        pagination={false}
        rowKey={(record) => record._id}
      />
    );
  };

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        dataSource={data as any[]}
        loading={loading}
        rowKey={(record) => record._id}
        expandable={{ expandedRowRender }}
        onChange={(pagination, filters, sorter) => {
          setTableFilters(filters);
          
          // Apply filters to data
          let filteredData = [...data];
          
          // Apply column filters
          Object.keys(filters).forEach((key) => {
            const filterValues = filters[key];
            if (filterValues && filterValues.length > 0) {
              filteredData = filteredData.filter((item: any) => {
                // Handle nested properties like majorMed.status
                const value = key.includes('.') 
                  ? key.split('.').reduce((obj, k) => obj?.[k], item)
                  : item[key];
                return filterValues.includes(value);
              });
            }
          });
          
          // Notify parent of filtered data
          onFilteredDataChange?.(filteredData);
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          onShowSizeChange: (current, size) => setPageSize(size),
          onChange: (page) => setCurrentPage(page),
        }}
      />
    </div>
  );
};

export default PoliciesTable;
